import { datadogLogs } from "@datadog/browser-logs";
import { useHandler } from "@redotech/react-util/hook";
import { useWaitForPrevious } from "@redotech/react-util/wait-for-previous";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import ChevronUpIcon from "@redotech/redo-icons/chevron-up.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { EnqueuePrintJobItem } from "@redotech/redo-merchant-fulfillment-rpc/schema/enqueue-print-jobs";
import { PrintQueueStatus } from "@redotech/redo-merchant-fulfillment-rpc/schema/watch-print-queue";
import {
  getHumanReadablePrintJobError,
  isRetryablePrintJobError,
  PrintableDocument,
  PrintJobStatus,
  PrintStatusLabels,
  RedoPrintJob,
  RedoPrintJobEvent,
} from "@redotech/redo-model/fulfillments/print-job";
import { showNotification } from "@redotech/redo-web/alert";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoProgressBar } from "@redotech/redo-web/arbiter-components/progress-bar/redo-progress-bar";
import { Flex } from "@redotech/redo-web/flex";
import { dismissNotificationByKey } from "@redotech/redo-web/notification-provider";
import { Text } from "@redotech/redo-web/text";
import {
  createContext,
  memo,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Subject } from "rxjs";
import { useObserveStream } from "../hooks/use-watch-stream";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";
import { PrintableBatch } from "./print-batch-modal";
import {
  usePrintQueueEnabled,
  useStreamingEnabled,
} from "./streaming-enabled-context";

interface PrintQueue {
  addJob: (job: EnqueuePrintJobItem) => void;
  enqueuePending: boolean;
  observePrintQueue: Subject<PrintQueueStatus>;
  suppressNotifications: () => () => void;
}

const PrintJobStatusOrder: Record<PrintJobStatus, number> = {
  [PrintJobStatus.CREATED]: 0,
  [PrintJobStatus.PURCHASED]: 0,
  [PrintJobStatus.GENERATING]: 1,
  [PrintJobStatus.GENERATED]: 1,
  [PrintJobStatus.INITIALIZING_PRINTING]: 2,
  [PrintJobStatus.SENT_TO_PRINT_NODE]: 3,
  [PrintJobStatus.PRINT_NODE_CONFIRMED]: 4,
  [PrintJobStatus.SENT_TO_PRINTER]: 5,
  [PrintJobStatus.COMPLETED]: 6,
  [PrintJobStatus.BATCH_COMPLETED]: 6,
  [PrintJobStatus.FAILED]: 6,
  [PrintJobStatus.CANCELLING]: 6,
  [PrintJobStatus.CANCELLED]: 6,
};

export const PrintQueueContext = createContext<PrintQueue | null>(null);

export const SuppressPrintNotificationsProvider = memo(
  function SuppressPrintNotificationsProvider({
    children,
  }: {
    children: ReactNode;
  }) {
    const printQueue = useContext(PrintQueueContext);

    useEffect(() => {
      if (!printQueue) {
        return;
      }
      const unsuppress = printQueue.suppressNotifications();
      return unsuppress;
    }, [printQueue]);

    return <>{children}</>;
  },
);

export const PrintQueueProvider = memo(function PrintQueueProvider({
  onDocumentPrinted,
  children,
}: {
  onDocumentPrinted?: (
    batch?: PrintableBatch,
    document?: PrintableDocument,
  ) => void;
  children: ReactNode;
}) {
  const client = useContext(RedoOutboundLabelsRpcClientContext);
  const team = useContext(TeamContext);
  const isOmsEnabled = team?.settings.outboundLabels?.newOmsEnabled;
  const streamingEnabled = useStreamingEnabled();
  const printQueueEnabled = usePrintQueueEnabled();
  const [enqueuePending, setEnqueuePending] = useState(false);

  const observePrintQueue = useObserveStream(
    isOmsEnabled && streamingEnabled && printQueueEnabled ? client : undefined,
    "watchPrintQueue",
    {},
    { total: 0, completed: 0, failed: [], inFlight: [] },
    ({ inFlight: oldInFlight }, { inFlight: newInFlight }) => {
      if (
        !oldInFlight.every((job) => newInFlight.find((j) => j._id === job._id))
      ) {
        onDocumentPrinted?.();
      }
    },
  );

  // Fallback polling mechanism in case streaming fails
  const pollStatus = useWaitForPrevious(async () => {
    if (!client || !isOmsEnabled || !streamingEnabled || !printQueueEnabled) {
      return;
    }
    try {
      const status = await client.getPrintQueueStatus({});
      observePrintQueue.next(status);
    } catch (error) {
      datadogLogs.logger.error("Failed to poll print queue status", { error });
    }
  });

  useEffect(() => {
    if (!client || !isOmsEnabled || !streamingEnabled || !printQueueEnabled) {
      return;
    }

    const POLL_INTERVAL = 15_000;
    const intervalId = setInterval(pollStatus, POLL_INTERVAL);

    return () => clearInterval(intervalId);
  }, [client, isOmsEnabled, pollStatus, streamingEnabled, printQueueEnabled]);

  const retryJob = useHandler(async (job: EnqueuePrintJobItem) => {
    try {
      setEnqueuePending(true);
      if (!client) {
        console.error("No client found for enqueuePrintJobs");
        return;
      }

      const status = await client.enqueuePrintJobs([job]);
      observePrintQueue.next(status);
    } catch (error) {
      console.error("Failed to enqueue print job:", error);
    } finally {
      setEnqueuePending(false);
    }
  });

  const cancelJob = useHandler(async (job: EnqueuePrintJobItem) => {
    datadogLogs.logger.info("Cancelling print job", { job });
    try {
      if (!client) {
        console.error("No client found for cancelJob");
        return;
      }

      const status = await client.cancelPrintJobs({ printJobs: [job._id!] });
      observePrintQueue.next(status);
    } catch (error) {
      console.error("Failed to cancel print job:", error);
    }
  });

  const addJob = useHandler(async (job: EnqueuePrintJobItem) => {
    try {
      setEnqueuePending(true);

      if (!client) {
        console.error("No client found for enqueuePrintJobs");
        return;
      }

      const status = await client.enqueuePrintJobs([job]);
      observePrintQueue.next(status);
    } catch (error) {
      console.error("Failed to enqueue print job:", error);
    } finally {
      setEnqueuePending(false);
    }
  });

  const suppressionCount = useRef<number>(0);

  const suppressNotifications = useHandler(() => {
    suppressionCount.current += 1;
    if (suppressionCount.current === 1) {
      dismissNotificationByKey("print-status");
      printStatusNotification.current = false;
    }
    return () => {
      suppressionCount.current -= 1;
    };
  });

  const printStatusNotification = useRef<boolean>(false);
  useEffect(() => {
    const subscription = observePrintQueue.subscribe((status) => {
      if (suppressionCount.current > 0) {
        return;
      }
      if (status.inFlight.length > 0 && !printStatusNotification.current) {
        showNotification({
          dataTestId: "print-status",
          hideCloseX: true,
          noPadding: true,
          notificationKey: "print-status",
          onDismiss: () => {
            printStatusNotification.current = false;
          },
          children: (dismiss) => (
            <PrintStatusDisplay
              dismiss={dismiss}
              initialJobs={status.inFlight}
              observePrintQueue={observePrintQueue}
              onDismissJob={cancelJob}
              onRetryJob={retryJob}
            />
          ),
        });

        printStatusNotification.current = true;
      } else if (status.total === 0 && printStatusNotification.current) {
        dismissNotificationByKey("print-status");
        printStatusNotification.current = false;
      }
    });
    return () => subscription.unsubscribe();
  }, [observePrintQueue, cancelJob, retryJob]);

  const contextValue = useMemo(
    () => ({
      addJob,
      enqueuePending,
      observePrintQueue,
      suppressNotifications,
    }),
    [addJob, enqueuePending, observePrintQueue, suppressNotifications],
  );

  return (
    <PrintQueueContext.Provider value={contextValue}>
      {children}
    </PrintQueueContext.Provider>
  );
});

const PrintStatusDisplay = memo(function PrintStatusDisplay({
  observePrintQueue,
  dismiss,
  initialJobs,
  onDismissJob,
  onRetryJob,
}: {
  observePrintQueue: Subject<PrintQueueStatus>;
  dismiss: () => void;
  initialJobs: RedoPrintJob[];
  onDismissJob?: (job: RedoPrintJob) => void;
  onRetryJob?: (job: RedoPrintJob) => void;
}) {
  const [jobs, setJobs] = useState<RedoPrintJob[]>(initialJobs);

  useEffect(() => {
    const subscription = observePrintQueue.subscribe(({ inFlight }) =>
      setJobs(inFlight),
    );
    return () => subscription.unsubscribe();
  }, [observePrintQueue]);

  const [expanded, setExpanded] = useState(false);

  const inFlightJobs = jobs
    .filter(
      ({ status }) =>
        status === PrintJobStatus.FAILED ||
        PrintJobStatusOrder[status] /
          PrintJobStatusOrder[PrintJobStatus.COMPLETED] <
          1,
    )
    .sort((a, b) => a.createdAt!.valueOf() - b.createdAt!.valueOf());
  const [ellipsis, setEllipsis] = useState(".");
  useEffect(() => {
    const interval = setInterval(() => {
      setEllipsis((ellipsis) => {
        if (ellipsis.length >= 3) {
          return ".";
        }
        return ellipsis + ".";
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  if (inFlightJobs.length === 0) {
    dismiss();
    return null;
  }

  const failedJobs = inFlightJobs.filter(
    ({ status }) => status === PrintJobStatus.FAILED,
  );

  return (
    <Flex dir="column" gap="none">
      <Flex align="center" justify="space-between" p="xl" py="md">
        <Text fontSize="sm" fontWeight="semibold">
          Printing {inFlightJobs.length} job{inFlightJobs.length !== 1 && "s"}
          {ellipsis}
        </Text>
        <Flex align="center" gap="sm">
          {failedJobs.length > 0 && (
            <RedoBadge color="error" text={`${failedJobs.length} failed`} />
          )}
          <RedoButton
            IconTrailing={expanded ? ChevronUpIcon : ChevronDownIcon}
            onClick={() => setExpanded(!expanded)}
            size="xs"
          />
        </Flex>
      </Flex>

      {expanded && (
        <Flex
          dir="column"
          gap="none"
          maxHeight="quarter-screen"
          overflow="auto"
        >
          {inFlightJobs.map((job: RedoPrintJob, index) => {
            let message = "";
            let retryable = false;
            if (job.status === PrintJobStatus.FAILED) {
              datadogLogs.logger.info("Print job failed", { job });
              const eventMessage = job.events.findLast(
                (event: RedoPrintJobEvent) =>
                  event.status === PrintJobStatus.FAILED,
              )?.message;
              message =
                getHumanReadablePrintJobError(eventMessage) ?? "Unknown error";
              retryable = isRetryablePrintJobError(eventMessage);
            }
            const isFailed = job.status === PrintJobStatus.FAILED;
            return (
              <Flex
                borderColor="secondary"
                borderStyle="solid"
                borderTopWidth="1px"
                dir="column"
                gap="sm"
                key={job._id}
                p="md"
              >
                <Flex align="center" justify="space-between">
                  <Text fontSize="xs">
                    Print job {index + 1}: {PrintStatusLabels[job.status]}
                  </Text>
                  {isFailed && (
                    <Flex align="center" gap="sm">
                      <RedoButton
                        hierarchy="tertiary"
                        onClick={() => {
                          onDismissJob?.(job);
                        }}
                        size="sm"
                        text="Dismiss"
                        theme="normal"
                      />
                      {retryable && (
                        <RedoButton
                          hierarchy="primary"
                          onClick={() => {
                            onRetryJob?.(job);
                          }}
                          size="sm"
                          text="Retry"
                          theme="normal"
                        />
                      )}
                    </Flex>
                  )}
                </Flex>
                {isFailed ? (
                  <Text fontSize="xs" textColor="error">
                    {message}
                  </Text>
                ) : (
                  <RedoProgressBar
                    labelStyle="none"
                    percentage={Math.floor(
                      (PrintJobStatusOrder[job.status] /
                        PrintJobStatusOrder[PrintJobStatus.COMPLETED]) *
                        100,
                    )}
                    style="thin"
                  />
                )}
              </Flex>
            );
          })}
        </Flex>
      )}
    </Flex>
  );
});
